import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  input,
  model,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { OrgLogoComponent } from '../org-logo/org-logo';
import { ToolbarSearchComponent } from '../toolbar-search/toolbar-search';
import { createExpansionHandler } from '../../shared/expansion-handler';
import { VendorApi } from '../../services/vendor-api/vendor-api';
import {
  VENDOR_TYPE_LABELS,
  VendorVerificationState,
  countryFlagByCode,
  countryNameByCode,
  type VendorMetadata,
  type VendorProduct,
  type VendorUse,
} from '../../models';
import {
  bucketUsageState,
  getVendorLogoUrl,
  resolveVendorMeta,
  resolveVendorName,
} from '../../utils/vendor.utils';

/** Per-row literal — flattens registry + use + selection state so the
 *  template renders directly without nested lookups. The picker filters
 *  to ACTIVE-bucket relationships before building rows, so no usage-state
 *  fields surface to the row: "approved" is implicit. */
interface MemberRow {
  vendorId: string;
  name: string;
  /** True when the vendor's global-registry entry is in the VERIFIED
   *  state — rendered as the purple-tick mark next to the name (same
   *  glyph the registry catalogue uses). Org-level "approval" is already
   *  implied by the picker's ACTIVE-bucket filter, so this is the only
   *  state worth surfacing here. */
  verified: boolean;
  logoUrl: string | null;
  typeLabel: string;
  country: string | null;
  countryFlag: string | null;
  selected: boolean;
  expanded: boolean;
  countryName: string;
  loading: boolean;
  failed: boolean;
  allProducts: boolean;
  productCount: number;
  scopeLabelKey: string;
  products: readonly (VendorProduct & { selected: boolean })[];
}

/**
 * Vendor-list member picker — the single source for the
 * "pick which vendors (and optionally which of their products)
 * this list applies to" UI primitive.
 *
 * Consumed identically by:
 *   - `<app-vendor-list-modal>` — create-flow wizard, step 2
 *   - `<app-vendor-list-detail-panel>` — edit-flow drawer
 *
 * Scope rules (enforced here so callers don't repeat them):
 *   - Only ACTIVE-bucket relationships (APPROVED + CONDITIONALLY_APPROVED)
 *     are eligible for list membership. Non-active rows are filtered out
 *     at the row level so the user only sees actionable choices.
 *   - Per-vendor product picks default to the "all products" sentinel
 *     (empty array). Toggling individual products explodes the array;
 *     unticking the last product drops the vendor entirely.
 *
 * I/O contract:
 *   - `vendors` — the org's full inventory (caller passes it through;
 *     filtering by bucket happens inside).
 *   - `registry` — vendor-metadata lookup pool for logo / name / type.
 *   - `selection` (two-way `model`) — `Map<vendor_id, product_ids[]>`.
 *     The caller owns the canonical selection signal; this picker just
 *     mutates it through `model.update()`.
 *
 * Product fetching is lazy + memoised inside the picker (one fetch per
 * vendor on first selection / first expand), so opening it on a large
 * inventory isn't an N-request fan-out.
 */
@Component({
  selector: 'app-vendor-member-picker',
  imports: [MatIconModule, TranslocoModule, OrgLogoComponent, ToolbarSearchComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './vendor-member-picker.html',
  styleUrl: './vendor-member-picker.css',
})
export class VendorMemberPicker {
  private readonly vendorApi = inject(VendorApi);
  private readonly destroyRef = inject(DestroyRef);
  private readonly vendorButtons = viewChildren<ElementRef<HTMLButtonElement>>('vendorToggle');

  readonly vendors = input.required<readonly VendorUse[]>();
  readonly registry = input<readonly VendorMetadata[]>([]);

  /** Two-way bound selection. Use `[(selection)]` at the call site to
   *  thread changes into the host's signal. */
  readonly selection = model<Map<string, string[]>>(new Map());

  /** Bound to the search input. Exposed as a local signal because the
   *  filter is purely visual — no two-way contract with the caller. */
  protected readonly search = signal('');

  /** Per-vendor product cache. Populated on demand via `fetchProductsIfNeeded`.
   *  Misses route through `productLoading` so the row shows "Loading…"
   *  while in flight; the eventual array is the source of truth for the
   *  product options. */
  protected readonly productCache = signal<Map<string, readonly VendorProduct[]>>(new Map());
  protected readonly productLoading = signal<Set<string>>(new Set());
  protected readonly productErrors = signal<Set<string>>(new Set());

  /** Which selected vendors are currently showing their product drawer. */
  protected readonly vendorExpansion = createExpansionHandler();

  // ─── Joins ──────────────────────────────────────────────────────────────

  private readonly registryById = computed(() => {
    const map = new Map<string, VendorMetadata>();
    for (const v of this.registry()) map.set(v.vendor_id, v);
    return map;
  });

  protected readonly rows = computed<readonly MemberRow[]>(() => {
    const q = this.search().trim().toLowerCase();
    const selected = this.selection();
    const expanded = this.vendorExpansion.expanded();
    const productMap = this.productCache();
    const lookup = this.registryById();

    const filter = (use: VendorUse): boolean => {
      if (!q) return true;
      return resolveVendorName(use, lookup).toLowerCase().includes(q);
    };

    const rows: MemberRow[] = this.vendors()
      .filter(use => bucketUsageState(use) === 'ACTIVE')
      .filter(filter)
      .map(use => {
        const meta = resolveVendorMeta(use, lookup);
        const country = meta?.headquarters_country ?? null;
        const productCount = selected.get(use.vendor_id)?.length ?? 0;
        return {
          vendorId: use.vendor_id,
          name: meta?.name ?? use.vendor_id,
          verified: meta?.current_verification_state === VendorVerificationState.VERIFIED,
          logoUrl: meta?.website ? getVendorLogoUrl(meta.website) : null,
          typeLabel: meta ? VENDOR_TYPE_LABELS[meta.vendor_type] : '',
          country,
          countryFlag: country ? countryFlagByCode(country) : null,
          selected: selected.has(use.vendor_id),
          expanded: expanded.has(use.vendor_id),
          countryName: country ? countryNameByCode(country) : '',
          loading: this.productLoading().has(use.vendor_id),
          failed: this.productErrors().has(use.vendor_id),
          allProducts: productCount === 0,
          productCount,
          scopeLabelKey: this.productScopeLabelKey(productCount),
          products: (productMap.get(use.vendor_id) ?? []).map(product => ({
            ...product,
            selected: this.isProductSelected(use.vendor_id, product.product_id),
          })),
        };
      });

    // Keep rows stable while selecting so keyboard focus and pointer targets do not move.
    return rows;
  });

  protected readonly selectedCount = computed(() => this.selection().size);
  protected readonly hasMatches = computed(() => this.rows().length > 0);

  private productScopeLabelKey(count: number): string {
    if (count === 0) return 'vendor_member_picker.all_products';
    if (count === 1) return 'vendor_member_picker.product_one';
    return 'vendor_member_picker.product_count';
  }

  protected selectAllProducts(vendorId: string): void {
    if (!this.selection().has(vendorId)) return;
    this.selection.update(current => new Map(current).set(vendorId, []));
  }

  // ─── Selection API ─────────────────────────────────────────────────────

  protected isProductSelected(vendorId: string, productId: string): boolean {
    const ids = this.selection().get(vendorId);
    if (!ids) return false;
    return ids.length === 0 || ids.includes(productId);
  }

  protected getProducts(vendorId: string): readonly VendorProduct[] {
    return this.productCache().get(vendorId) ?? [];
  }

  protected toggleVendor(vendorId: string): void {
    const wasSelected = this.selection().has(vendorId);
    this.selection.update(curr => {
      const next = new Map(curr);
      if (wasSelected) {
        next.delete(vendorId);
      } else {
        next.set(vendorId, []);
      }
      return next;
    });
    this.vendorExpansion.set(vendorId, false);
    if (!wasSelected) this.fetchProductsIfNeeded(vendorId);
  }

  protected toggleExpand(vendorId: string, event: Event): void {
    event.stopPropagation();
    if (!this.selection().has(vendorId)) return;
    const wasExpanded = this.vendorExpansion.isExpanded(vendorId);
    this.vendorExpansion.toggle(vendorId);
    if (!wasExpanded) this.fetchProductsIfNeeded(vendorId);
  }

  protected toggleProduct(vendorId: string, productId: string): void {
    const products = this.getProducts(vendorId);
    if (products.length === 0) return;
    const currentIds = this.selection().get(vendorId);
    if (!currentIds) return;

    // Expand the "all products" sentinel before subtracting one.
    const expanded = currentIds.length === 0 ? products.map(p => p.product_id) : currentIds;
    let nextIds = expanded.includes(productId)
      ? expanded.filter(id => id !== productId)
      : [...expanded, productId];
    const vendorWillBeDropped = nextIds.length === 0;
    if (products.every(product => nextIds.includes(product.product_id))) nextIds = [];
    this.selection.update(curr => {
      const next = new Map(curr);
      if (vendorWillBeDropped) {
        next.delete(vendorId);
      } else {
        next.set(vendorId, nextIds);
      }
      return next;
    });
    if (vendorWillBeDropped) {
      this.vendorExpansion.set(vendorId, false);
      this.vendorButtons()
        .find(button => button.nativeElement.dataset['vendorId'] === vendorId)
        ?.nativeElement.focus();
    }
  }

  // ─── Data loader ───────────────────────────────────────────────────────

  /**
   * Fetch a vendor's products into the cache on first need. Idempotent —
   * subsequent calls are no-ops once the cache has the entry (or a fetch
   * is in flight).
   *
   * Exposed as a public method so callers (e.g. the drawer hydrating from
   * an existing list) can prime the cache for each pre-selected vendor
   * after they set the `selection` input. Without it, the product
   * sub-picker would render "Loading…" the first time the user expands
   * a pre-existing pick.
   */
  fetchProductsIfNeeded(vendorId: string): void {
    if (this.productCache().has(vendorId)) return;
    if (this.productLoading().has(vendorId)) return;
    this.productLoading.update(s => new Set(s).add(vendorId));
    this.productErrors.update(current => {
      const next = new Set(current);
      next.delete(vendorId);
      return next;
    });
    this.vendorApi
      .listVendorProducts(vendorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: products => {
          this.productCache.update(m => new Map(m).set(vendorId, products));
          this.clearProductLoading(vendorId);
        },
        error: () => {
          this.productErrors.update(current => new Set(current).add(vendorId));
          this.clearProductLoading(vendorId);
        },
      });
  }

  private clearProductLoading(vendorId: string): void {
    this.productLoading.update(s => {
      const n = new Set(s);
      n.delete(vendorId);
      return n;
    });
  }
}
